// Teach mode: a projector-ready stage timer. Space = start/pause · ← → = stage · F = full screen · Esc = leave.
import { useCallback, useEffect, useRef, useState } from "react";
import { getCatalogLesson } from "../../shared/catalog";
import { docFromAdaptation, docFromLesson, type LessonDoc } from "../../shared/lesson-model";
import { api } from "../lib/api";
import { Link, navigate, useLocation } from "../lib/router";
import { StageStrip } from "../components/StageStrip";
import { Icon } from "../components/Icon";

function chime() {
  try {
    const ctx = new AudioContext();
    [660, 880].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.22);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.22 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.22 + 0.5);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.22);
      o.stop(ctx.currentTime + i * 0.22 + 0.55);
    });
  } catch {
    /* audio unavailable */
  }
}

const fmt = (s: number) => {
  const neg = s < 0;
  const a = Math.abs(Math.round(s));
  return `${neg ? "+" : ""}${Math.floor(a / 60)}:${String(a % 60).padStart(2, "0")}`;
};

export default function Teach({ kind, id }: { kind: string; id: string }) {
  const { search } = useLocation();
  const [doc, setDoc] = useState<LessonDoc | null>(null);
  const [error, setError] = useState("");
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0); // seconds into current stage
  const [running, setRunning] = useState(false);
  const [sound, setSound] = useState(true);
  const startedAt = useRef<number | null>(null);
  const base = useRef(0);
  const chimed = useRef(false);

  useEffect(() => {
    const fail = (e: { code?: string; message: string }) =>
      setError(e.code === "NO_ACCESS" || e.code === "SIGN_IN_REQUIRED" ? "Teach mode needs an active free trial or subscription." : e.message);
    if (kind === "lesson") {
      api.lesson(id).then(({ lesson }) => setDoc(docFromLesson(lesson, Number(search.get("minutes")) || lesson.duration))).catch(fail);
    } else if (kind === "s") {
      api.shared(id).then(({ adaptation, preview }) => {
        if (preview) setError("Teach mode needs an active free trial or subscription.");
        else setDoc(docFromAdaptation(getCatalogLesson(adaptation.lessonId)!, adaptation.output));
      }).catch(fail);
    } else {
      api.adaptation(id).then(({ adaptation }) => setDoc(docFromAdaptation(getCatalogLesson(adaptation.lessonId)!, adaptation.output))).catch(fail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      if (startedAt.current !== null) setElapsed(base.current + (Date.now() - startedAt.current) / 1000);
    }, 250);
    return () => clearInterval(t);
  }, [running]);

  const stage = doc?.stages[idx];
  const left = stage ? stage.minutes * 60 - elapsed : 0;
  useEffect(() => {
    if (stage && left <= 0 && !chimed.current && running) {
      chimed.current = true;
      if (sound) chime();
    }
  }, [left, stage, running, sound]);

  const toggle = useCallback(() => {
    if (running) {
      base.current = elapsed;
      startedAt.current = null;
      setRunning(false);
    } else {
      startedAt.current = Date.now();
      setRunning(true);
    }
  }, [running, elapsed]);

  const go = useCallback(
    (d: number) => {
      if (!doc) return;
      const n = Math.max(0, Math.min(doc.stages.length - 1, idx + d));
      if (n === idx) return;
      setIdx(n);
      setElapsed(0);
      base.current = 0;
      chimed.current = false;
      startedAt.current = running ? Date.now() : null;
    },
    [doc, idx, running],
  );

  const exit = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    if (history.length > 1) history.back();
    else navigate(kind === "lesson" ? `/lesson/${id}` : kind === "s" ? `/s/${id}` : `/a/${id}`);
  }, [kind, id]);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === " ") { e.preventDefault(); toggle(); }
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key.toLowerCase() === "f") void (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen());
      else if (e.key === "Escape" && !document.fullscreenElement) exit();
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [toggle, go, exit]);

  if (error) return <div className="teach teach-msg"><p>{error}</p><p><Link to="/pricing">See plans</Link> · <Link to="/">Back to lessons</Link></p></div>;
  if (!doc || !stage) return <div className="teach teach-msg"><p>Loading…</p></div>;

  const over = left < 0;
  const nextStage = doc.stages[idx + 1];
  return (
    <div className={`teach teach-${stage.kind}${over ? " is-over" : ""}`}>
      <header className="teach-top">
        <p className="teach-lesson"><span className="code">{doc.code}</span> {doc.title}</p>
        <div className="teach-tools">
          <button className="tbtn" onClick={() => setSound((s) => !s)} aria-pressed={sound}>{sound ? "Chime on" : "Chime off"}</button>
          <button className="tbtn" onClick={() => void (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen())} aria-label="Full screen (F)"><Icon name="full" /></button>
          <button className="tbtn" onClick={exit} aria-label="Leave teach mode (Esc)"><Icon name="x" /></button>
        </div>
      </header>

      <StageStrip stages={doc.stages} size="xl" active={idx} progress={Math.max(0, elapsed / (stage.minutes * 60))} />

      <section className="teach-now">
        <div className="teach-stage">
          <p className="teach-step">Stage {idx + 1} of {doc.stages.length}{stage.interaction ? ` · ${stage.interaction}` : ""}</p>
          <h1>{stage.name}</h1>
        </div>
        <p className="teach-clock" aria-live="off" aria-label={over ? `${fmt(-left)} over time` : `${fmt(left)} remaining`}>{fmt(left)}</p>
      </section>

      <section className="teach-say">
        <p>{stage.teacher}</p>
        {stage.students && <p className="teach-students">{stage.students}</p>}
      </section>

      <footer className="teach-controls">
        <button className="tbtn" onClick={() => go(-1)} disabled={idx === 0}><Icon name="back" /> Previous</button>
        <button className="tbtn tbtn-main" onClick={toggle}>
          <Icon name={running ? "pause" : "play"} /> {running ? "Pause" : elapsed > 0 ? "Resume" : "Start"}
        </button>
        <button className="tbtn" onClick={() => go(1)} disabled={!nextStage}>
          {nextStage ? <>Next: {nextStage.name} <Icon name="next" /></> : "Last stage"}
        </button>
      </footer>
      <p className="teach-keys">Space start/pause · ← → change stage · F full screen · Esc leave</p>
    </div>
  );
}
